import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Box, Text, Grid, Flex } from 'theme-ui';
import { clamp } from 'ramda';
import Modal from 'shared/Modal';
import { TrimModalProps } from './types';
import useAsync from 'react-use/lib/useAsync';
import useScratch from 'react-use/lib/useScratch';
import useToggle from 'react-use/lib/useToggle';
import { BORDER_WIDTH, HANDLE_WIDTH, Range, RangeProps } from 'shared/Range';
import { TimeScale } from 'shared/TimeScale';
import Icon, { IconTypes } from 'shared/Icon';
import { throttle } from 'throttle-debounce';
import { getImageAspectRatio } from 'utils/getAspectRatio';
import { MAX_PREVIEW_HEIGHT } from 'constants/trim';
import TimeChooser from '../../../assets/time-chooser.svg';
import { getTime, msToSMPTE } from 'utils/formatTime';
import { CONTENT_TEAM_MODE } from 'utils/env';
import { useTranslator } from '@picsart/rc/services/localization';
import { MIN_SCENE_DURATION } from 'constants/scenes';

const VideoTrimModal: FC<TrimModalProps> = ({
  src,
  opened,
  duration,
  maxDuration,
  closeHandler,
  trimStart,
  applyHandler: applyFn
}) => {
  const [start, setStart] = useState(0);
  const [end, setEnd] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [paused, togglePaused] = useToggle(true);
  const smpte = useRef<string>('');
  const [framerate, setFramerate] = useState<string>('30');
  const translator = useTranslator();
  const { value: aspectRatio } = useAsync(() => getImageAspectRatio(src), [src]);

  const videoSize =
    aspectRatio < 1
      ? { width: 'auto', height: MAX_PREVIEW_HEIGHT }
      : { width: '100%', height: 'auto', maxHeight: MAX_PREVIEW_HEIGHT };

  const [timeSliderRef, timeSliderState] = useScratch();

  const throttledSetVideoTime = useCallback(
    throttle(200, (time: number) => {
      videoRef.current.currentTime = time;
    }),
    [videoRef.current]
  );

  useEffect(() => {
    if (!CONTENT_TEAM_MODE) return;
    const fps: number = parseFloat(framerate) || 30;
    smpte.current = msToSMPTE(currentTime * 1000, fps);
  }, [currentTime, framerate]);

  useEffect(() => {
    if (!videoRef.current) return;
    if (timeSliderState.isScratching) {
      const val = Math.fround(
        clamp(
          start,
          end,
          ((timeSliderState.x + timeSliderState.dx) / timeSliderState.elW) * maxDuration
        ) / 1000
      );
      if (!Number.isNaN(val)) {
        setCurrentTime(val);
        throttledSetVideoTime(val);
      }
    }
  }, [timeSliderState.dx, maxDuration]);

  useEffect(() => {
    if (!opened) {
      togglePaused(true);
    }
    if (!videoRef.current) return;
    setStart(trimStart);
    setEnd(duration + trimStart);
    setCurrentTime(trimStart / 1000);
    videoRef.current.currentTime = trimStart / 1000;
    videoRef.current.volume = 0.5;
  }, [opened]);

  useEffect(() => {
    if (!videoRef.current) return;
    if (currentTime * 1000 <= start) {
      setCurrentTime(start / 1000);
      throttledSetVideoTime(start / 1000);
    }
  }, [start]);

  useEffect(() => {
    if (!videoRef.current) return;
    if (currentTime * 1000 >= end) {
      setCurrentTime(end / 1000);
      throttledSetVideoTime(end / 1000);
    }
  }, [end]);

  const displayDuration = useMemo(() => getTime(end - start), [end, start]);

  const trimHandler = useCallback<RangeProps['onChange']>((startVal, endVal) => {
    setStart(startVal);
    setEnd(endVal);
  }, []);

  const applyHandler = () => {
    applyFn(end - start, start);
    closeHandler();
  };

  const togglePlay = () => {
    if (!videoRef.current) return;

    if (videoRef.current.paused) {
      if (currentTime * 1000 > end || currentTime * 1000 < start) {
        videoRef.current.currentTime = start / 1000;
      }

      videoRef.current.play();
      togglePaused(false);
    } else {
      videoRef.current.pause();
      togglePaused(true);
    }
  };

  const videoEndHandler = () => {
    togglePaused(true);
    setCurrentTime(start / 1000);
    throttledSetVideoTime(start / 1000);
  };

  const videoTimeUpdateHandler = (e: any) => {
    const { currentTime: time, paused: videoPaused } = e.target;

    if (!videoPaused) {
      if (time * 1000 > end) {
        videoRef.current.pause();
        videoEndHandler();
      } else {
        setCurrentTime(time);
      }
    }
  };

  return (
    <Modal
      color="transparent"
      opened={opened}
      closeHandler={closeHandler}
      title={translator('Trim_Tool')}
    >
      <Box sx={{ maxWidth: 1052, minWidth: 500 }} data-test="trim-modal">
        <video
          src={src}
          muted={false}
          sx={{ ...videoSize, objectFit: 'contain' }}
          ref={videoRef}
          onTimeUpdate={videoTimeUpdateHandler}
          onEnded={videoEndHandler}
        />
        {CONTENT_TEAM_MODE && (
          <Box sx={{ my: 4 }}>
            <Flex sx={{ my: 4 }}>
              <Flex>
                <Text sx={{ mb: 0, mr: 2 }} variant="modalBody">
                  FPS:
                </Text>
                <input
                  sx={{
                    width: '30px',
                    border: '1px solid rgba(0,0,0,.2)',
                    px: 1,
                    mr: 4,
                    borderRadius: '4px'
                  }}
                  value={framerate}
                  onChange={a => setFramerate(a.target.value)}
                  type="text"
                />
              </Flex>
              <Text sx={{ mb: 0 }} variant="modalBody">
                SMPTE: <span sx={{ fontFamily: 'monospace' }}>{smpte.current}</span>
              </Text>
            </Flex>
          </Box>
        )}
        <Grid columns={[3, '64px auto 80px']} gap={5} sx={{ mt: 4, mb: 6, userSelect: 'none' }}>
          <Button variant="outlined" sx={{ height: 40, py: 0 }} onClick={togglePlay}>
            <Icon
              name={paused ? 'play-editor' : 'pause-editor'}
              sx={{ mx: 'auto' }}
              type={IconTypes.Primary}
            />
          </Button>
          <Box mt={3} sx={{ position: 'relative' }}>
            <Range
              sx={{ mb: 2 }}
              minDiff={MIN_SCENE_DURATION}
              max={maxDuration}
              value1={start}
              value2={end}
              onChange={trimHandler}
            />
            <TimeScale min={0} max={maxDuration} pointsCount={5} formatFn={getTime} />
            <div
              data-test="trim-time_slider"
              ref={timeSliderRef}
              sx={{
                height: 0,
                left: HANDLE_WIDTH + BORDER_WIDTH,
                top: 0,
                width: `calc(100% - ${(HANDLE_WIDTH + BORDER_WIDTH) * 2}px)`,
                position: 'absolute'
              }}
            >
              <div
                sx={{
                  position: 'absolute',
                  width: 0,
                  transform: 'translateX(-5px)',
                  height: '34px',
                  top: '-14px'
                }}
                style={{
                  left: `${clamp(0, 1, (currentTime * 1000) / maxDuration) * 100}%`
                }}
              >
                <TimeChooser />
                <div sx={{ width: '12px', height: '100%', ml: '-6px' }} />
              </div>
            </div>
          </Box>
          <Text
            sx={{
              mt: 3,
              bg: '#222222',
              px: 4,
              color: '#fff',
              fontSize: 2,
              lineHeight: '24px',
              fontWeight: 'heading',
              height: 24,
              borderRadius: 'xs',
              userSelect: 'none'
            }}
          >
            {displayDuration}
          </Text>
        </Grid>
        <Button variant="primary" onClick={applyHandler} data-test="apply">
          {translator('Apply')}
        </Button>
      </Box>
    </Modal>
  );
};

VideoTrimModal.displayName = 'VideoTrimModal';

export default VideoTrimModal;
